import { captureAndLogSentryError } from "../sentry/client";

const SESSION_STORAGE_KEY = 'koin_cache';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour in milliseconds

/**
 * Load cached data from session storage
 * 
 * @param baseCurrency 
 * @param sessionStorageKey 
 * @returns 
 */
export function loadCachedData<T>(baseCurrency: string, sessionStorageKey: string = SESSION_STORAGE_KEY): T | null {
  try {
    const cached = sessionStorage.getItem(`${sessionStorageKey}_${baseCurrency}`);
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    const now = Date.now();
    const cacheAge = now - new Date(parsed.lastUpdated).getTime();

    // Return cached data if less than 1 hour old
    if (cacheAge < CACHE_TTL) {
      return parsed;
    }

    // Clear expired cache
    sessionStorage.removeItem(`${sessionStorageKey}_${baseCurrency}`);
    return null;
  } catch (error) {
    console.error('Error loading cached data:', error);
    captureAndLogSentryError(error, null);
    return null;
  }
};

// Save data to session storage
export function saveCachedData<T>(baseCurrency: string, data: T, sessionStorageKey: string = SESSION_STORAGE_KEY): void {
  try {
    sessionStorage.setItem(
      `${sessionStorageKey}_${baseCurrency}`,
      JSON.stringify(data)
    );
  } catch (error) {
    console.error('Error saving cached data to cache:', error);
    captureAndLogSentryError(error, null);
  }
};

// Use a safe localStorage reference that works in test environments
export const getStorage = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage;
  }
  if (typeof global !== 'undefined' && (global).localStorage) {
    return (global).localStorage;
  }
  // Fallback for test environments
  return {
    getItem: () => null,
    setItem: () => {},
    removeItem: () => {},
    clear: () => {},
  };
};